import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useAuth } from "../contexts/AuthContext";
import { apiClient } from "../lib/api";
import type { UserProfileUpdateRequest } from "../types/preferences";
import { getEffectiveDisplayName } from "../utils/profileUtils";

/**
 * Hook to manage user profile data
 */
export function useUserProfile() {
    const { user, loading: authLoading } = useAuth();
    const queryClient = useQueryClient();

    // Get user profile
    const {
        data: profile,
        isLoading,
        error,
    } = useQuery({
        queryKey: ["userProfile"],
        queryFn: async () => {
            const response = await apiClient.getUserProfile();
            return response.profile;
        },
        retry: 1,
        staleTime: 1000 * 60 * 5, // 5 minutes
        enabled: !!user && !authLoading, // Only fetch when user is authenticated
    });

    // Update user profile
    const updateProfileMutation = useMutation({
        mutationFn: async (profileData: UserProfileUpdateRequest) => {
            const response = await apiClient.updateUserProfile(profileData);
            return response.profile;
        },
        onSuccess: (updatedProfile) => {
            // Update the cache with the new profile data
            queryClient.setQueryData(["userProfile"], updatedProfile);
        },
        onError: (error) => {
            console.error("Failed to update profile:", error);
        },
    });

    return {
        profile,
        isLoading,
        error,
        updateProfile: updateProfileMutation.mutate,
        isUpdating: updateProfileMutation.isPending,
        updateError: updateProfileMutation.error,
        getEffectiveDisplayName: (userParam = user, profileParam = profile) =>
            getEffectiveDisplayName(userParam, profileParam ?? null),
    };
}
